import { DndContext, closestCorners } from '@dnd-kit/core'
import { SortableContext, arrayMove, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { Button, Icon, Input, Switch, Table, Tooltip } from 'antd'
import { CompareFn, SortOrder } from 'antd/lib/table'
import classNames from 'classnames'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import shallow from 'zustand/shallow'
import { PerAvatarPlus } from '../../components/custom_components/AvatarPlus'
import { PerEmpty } from '../../components/custom_components/Empty'
import PerFlexRow from '../../components/custom_components/FlexRow'
import PerSpacer from '../../components/custom_components/Spacer'
import { PerTag } from '../../components/custom_components/Tag'
import PerText from '../../components/custom_components/Text'
import { useAuth } from '../../context/auth-context'
import { NameDisplayPreference } from '../../graphql/enum/name-display-preference.enum'
import useHandleEmployeeTag from '../../graphql/ranking/hooks/useHandleEmployeeTag'
import { computeFullname } from '../../shared/nameDisplayPreferencesUtil'
import {
  EmployeeAndTag,
  EmployeeDisplayTinyInfo,
  EmployeeRankDto,
  EmployeeTagType,
  EmployeeWithRankAndTags,
  TalentsSeasonSettingsDto,
  TalentsSeasonStatus,
} from '../../types'
import { CompanySettingsRankingRemoveLowTagModal } from '../companySettings/ranking/RemoveLowTagModal'
import { useCultivatingTalentsStore } from './CultivatingTalentsStore'
import { DragHandle, DraggableTableRow } from './DraggableTableRow'
import { LazyRankingTagHistoryModal } from './LazyRankingTagHistoryModal'
import { RankingTableActionButtons } from './RankingTableActionButtons'
import './TalentsSeasonTable.less'
import { rankingUiProps } from './ranking.uiprops'
import PerIcon from '../../components/custom_components/Icon'
import { AddEntryLevelLowTagAssignmentModal } from '../companySettings/ranking/AddEntryLevelLowTagModal'

type Props = {
  data: EmployeeWithRankAndTags[]
  settings: TalentsSeasonSettingsDto
  isEditingProcess: boolean
  isHRSettings: boolean
  isAllCompaniesSettings: boolean
  isYourCompanyView: boolean
  isCompanyManager: boolean
  disclaimerClosed: boolean
  sortRanking: boolean
  toggleSortRanking: (value: boolean) => void
}

const updateEmployeeRankNumbers = (employeeList: EmployeeWithRankAndTags[]): EmployeeWithRankAndTags[] => {
  return employeeList.map((employee, index) => ({ ...employee, rank: index + 1 }))
}

const atLeastOneNotAssessableExists = (employeeList: EmployeeWithRankAndTags[]): boolean => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return employeeList.some((employee) => {
    return !employee.assessable
  })!
}

// This method assumes that there is at least one notAssessable employee
const firstNotAssessableIndex = (employeeList: EmployeeWithRankAndTags[]): number => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const firstNotAssessableEmployee: EmployeeWithRankAndTags = employeeList.find((employee) => {
    return !employee.assessable
  })!
  return firstNotAssessableEmployee.rank - 1
}

export const TalentsSeasonTable: FC<Props> = ({
  data,
  settings,
  isEditingProcess,
  isAllCompaniesSettings,
  isYourCompanyView,
  isHRSettings,
  disclaimerClosed,
  isCompanyManager,
  sortRanking,
  toggleSortRanking,
}) => {
  const [t] = useTranslation()
  const { user } = useAuth()

  const [tableData, setTableData] = useState<EmployeeWithRankAndTags[]>([])
  const [rankedEmployees, setRankedEmployees] = useCultivatingTalentsStore(
    (state) => [state.rankedEmployees, state.setRankedEmployees],
    shallow,
  )

  useEffect(() => {
    if (isEditingProcess) {
      setRankedEmployees(
        tableData.map((employee, index) => {
          return { ...employee, rank: index + 1 }
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditingProcess])

  const [modalOpenRemoveLow, setModalOpenRemoveLow] = useState<boolean>(false)
  const [modalDataRemoveLow, setModalDataRemoveLow] = useState<EmployeeAndTag | undefined>(undefined)
  const [modalDataAddEntryLevelLow, setModalDataAddEntryLevelLow] = useState<EmployeeAndTag | undefined>(undefined)
  const [modalOpenTagHistory, setModalOpenTagHistory] = useState<boolean>(false)
  const [modalTagHistoryEmployeeId, setModalTagHistoryEmployeeId] = useState<string | undefined>(undefined)
  const { tagColor, isPerformanceTag, isImpactOfLossTag, isRiskOfLossTag } = rankingUiProps()
  const { companyId, country, year, talentsSeasonId, talentsSeasonStatus, topPercentage } = settings
  const { removeEmployeeTag, employeeHasFutureEmploymentChange, employeeHasOngoingAppraisal } = useHandleEmployeeTag(
    companyId,
    country,
    year,
    talentsSeasonId,
  )

  const onRemoveLowModalCancel = () => {
    setModalOpenRemoveLow(false)
    setModalDataRemoveLow(undefined)
  }

  const onAddEntryLevelLowModalCancel = () => {
    setModalDataAddEntryLevelLow(undefined)
  }

  const onTagHistoryModalCancel = () => {
    setModalOpenTagHistory(false)
    setModalTagHistoryEmployeeId(undefined)
  }

  const handleDragEnd = useCallback(
    (event) => {
      const { active, over } = event
      if (over && active.id !== over.id) {
        const oldIndex = tableData.findIndex((item) => item.id === active.id)
        const newIndex = tableData.findIndex((item) => item.id === over.id)
        const isNewIndexAllowed = tableData[newIndex].assessable
        if (isNewIndexAllowed) {
          const updatedArray = arrayMove([...tableData], oldIndex, newIndex)
          const employeeListWithUpdatedRanks = updateEmployeeRankNumbers(updatedArray)
          setTableData(employeeListWithUpdatedRanks)
          setRankedEmployees(employeeListWithUpdatedRanks)
        }
      }
    },
    [tableData, setRankedEmployees],
  )

  const handleRankInputChange = useCallback(
    (oldIndex: number, newIndex: number, event?: any) => {
      if (event) {
        const notAssessableEmployeesExist: boolean = !!tableData.find((e) => {
          return !e.assessable
        })
          ? true
          : false
        const maxAssessableIndex = notAssessableEmployeesExist ? firstNotAssessableIndex(tableData) - 1 : tableData.length - 1
        if (newIndex >= maxAssessableIndex) {
          if (oldIndex === maxAssessableIndex) {
            if (event.currentTarget && event.currentTarget.value) {
              event.currentTarget.value = maxAssessableIndex + 1
            }
            return
          } else {
            newIndex = maxAssessableIndex
          }
        }
      }
      const updatedArray = arrayMove([...tableData], oldIndex, newIndex)
      const employeeListWithUpdatedRanks = updateEmployeeRankNumbers(updatedArray)

      setRankedEmployees(employeeListWithUpdatedRanks)
    },
    [tableData, setRankedEmployees],
  )

  const sortByEvaluation = useCallback(() => {
    const updatedArray = [...tableData].sort((a, b) => {
      const getEvaluation = (el) => {
        return el.evaluation && el.draftEvaluation
          ? Math.max(el.evaluation, el.draftEvaluation)
          : el.evaluation || el.draftEvaluation || null
      }

      const aEvaluation = getEvaluation(a)
      const bEvaluation = getEvaluation(b)
      const aFullName = computeFullname(user.nameDisplayPreference, a.employee.firstName, a.employee.lastName)
      const bFullName = computeFullname(user.nameDisplayPreference, b.employee.firstName, b.employee.lastName)

      if ((a.assessable && b.assessable) || (!a.assessable && !b.assessable)) {
        if (!aEvaluation && !bEvaluation) {
          if (!a.latestEvaluation && !b.latestEvaluation) return aFullName.localeCompare(bFullName)
          if (!a.latestEvaluation && b.latestEvaluation) return 1
          if (a.latestEvaluation && !b.latestEvaluation) return -1
          if (a.latestEvaluation !== b.latestEvaluation) return b.latestEvaluation! - a.latestEvaluation!
        } else {
          if (!aEvaluation && bEvaluation) return 1
          if (aEvaluation && !bEvaluation) return -1
          if (aEvaluation && bEvaluation && aEvaluation !== bEvaluation) return bEvaluation - aEvaluation

          if (!a.latestEvaluation && b.latestEvaluation) return 1
          if (!b.latestEvaluation && a.latestEvaluation) return -1
          if (a.latestEvaluation && b.latestEvaluation && a.latestEvaluation !== b.latestEvaluation)
            return b.latestEvaluation - a.latestEvaluation
          if (!b.latestEvaluation && !a.latestEvaluation) return aFullName.localeCompare(bFullName)
          if (a.latestEvaluation && b.latestEvaluation && a.latestEvaluation === b.latestEvaluation)
            return aFullName.localeCompare(bFullName)
        }
      }

      return a.assessable ? -1 : 1
    })

    const employeeListWithUpdatedRanks = updateEmployeeRankNumbers(updatedArray)
    setRankedEmployees(employeeListWithUpdatedRanks)
  }, [tableData, setRankedEmployees, user.nameDisplayPreference])

  useEffect(() => {
    setTableData(rankedEmployees)
    if (sortRanking) {
      toggleSortRanking(false)
      sortByEvaluation()
    }
  }, [rankedEmployees, sortByEvaluation, sortRanking, toggleSortRanking])

  const handleNotAssessableChange = useCallback(
    (assessable: boolean, employee: EmployeeWithRankAndTags) => {
      const oldIndex = employee.rank - 1
      let employeeListWithUpdatedRanks: EmployeeWithRankAndTags[]
      if (!assessable) {
        const newIndex = tableData.length - 1

        const updatedArray = arrayMove([...tableData], oldIndex, newIndex)
        employeeListWithUpdatedRanks = updateEmployeeRankNumbers(updatedArray)
        employeeListWithUpdatedRanks[newIndex] = { ...employeeListWithUpdatedRanks[newIndex], assessable }
      } else {
        let updatedArray = [...tableData]
        const newIndex = firstNotAssessableIndex(updatedArray)
        updatedArray[employee.rank - 1] = { ...employee, assessable }
        updatedArray = arrayMove([...updatedArray], oldIndex, newIndex)
        employeeListWithUpdatedRanks = updateEmployeeRankNumbers(updatedArray)
      }
      setTableData(employeeListWithUpdatedRanks)
      setRankedEmployees(employeeListWithUpdatedRanks)
    },
    [tableData, setTableData, setRankedEmployees],
  )

  const handleInputAndConfirmationOnEnterPress = useCallback((event: any) => {
    if (event.key === 'Enter') {
      event.target.blur()
    } else {
      let res = ''
      const chars = event.currentTarget.value.split('')
      for (const char of chars) {
        if (!isNaN(Number(char))) {
          res += char
        }
      }
      event.currentTarget.value = res
    }
  }, [])

  const handleInputConfirmationOnBlur = useCallback(
    (event: any, oldIndex: number) => {
      const parsedValue = Number(event.currentTarget.value)
      if (!isNaN(parsedValue) && parsedValue !== oldIndex + 1) {
        handleRankInputChange(oldIndex, parsedValue - 1, event)
      }
    },
    [handleRankInputChange],
  )

  const renderRankTooltip = (employeeName: string, year: number, previousCompanyRankData: EmployeeRankDto) => {
    return (
      <PerFlexRow direction="column" align="middle">
        <PerText fontSize="xs">
          {`${t('rankingView.table.ranking.rankTooltip.previousCompanyRankText', {
            employeeName: employeeName,
            year,
          })}:`}
        </PerText>
        <PerText fontSize="xs" mt="xxxs">
          {t('rankingView.table.ranking.rankTooltip.previousCompanyRankInfo', {
            rank: previousCompanyRankData.rank,
            total: previousCompanyRankData.total,
            companyName: previousCompanyRankData.companyName,
          })}
        </PerText>
      </PerFlexRow>
    )
  }

  const employeeTableHeader = <Tooltip title={t('employeeList.table.employeeTooltip')}>{t('rankingView.table.employee.heading')}</Tooltip>

  const dataColumns = [
    {
      key: 'ranking-view',
      title: t('rankingView.table.ranking.heading', { year: settings.year }),
      width: '9%',
      sorter: (employeeA: EmployeeWithRankAndTags, employeeB: EmployeeWithRankAndTags) => {
        return employeeA.rank - employeeB.rank
      },
      render: ({ rank, previousCompanyRankData, assessable, employee }: EmployeeWithRankAndTags) => {
        return (
          <PerFlexRow direction="column">
            <PerText fontWeight="bold">{assessable ? rank : '-'}</PerText>
            {!!previousCompanyRankData && (
              <>
                <PerSpacer mt="xxxs"></PerSpacer>
                <Tooltip title={renderRankTooltip(employee.firstName, settings.year, previousCompanyRankData)} placement="left">
                  <span style={{ fontSize: '10px' }}>
                    {t('rankingView.table.ranking.previousCompanyRank', {
                      rank: previousCompanyRankData.rank,
                      total: previousCompanyRankData.total,
                    })}
                  </span>
                </Tooltip>
              </>
            )}
          </PerFlexRow>
        )
      },
    },
    {
      key: 'ranking-edit',
      width: '16%',
      title: t('rankingView.table.ranking.heading', { year: settings.year }),
      className: 'minWidth-column-l',
      render: (_: EmployeeWithRankAndTags, employee: EmployeeWithRankAndTags, index: number) => {
        return (
          <PerFlexRow align="middle">
            {employee.assessable ? (
              <DragHandle />
            ) : (
              <button disabled className="drag-button-disabled">
                <Icon type="menu" />
              </button>
            )}
            <PerSpacer mr="xxs" />
            <div style={{ width: '52px' }}>
              {employee.assessable ? (
                <Input
                  style={{ width: '52px' }}
                  type="numeric"
                  defaultValue={employee.rank}
                  key={employee.rank}
                  min={1}
                  max={data.length}
                  onBlur={(e) => handleInputConfirmationOnBlur(e, index)}
                  onKeyUp={(e) => handleInputAndConfirmationOnEnterPress(e)}
                />
              ) : (
                <Input style={{ width: '52px' }} disabled />
              )}
            </div>
            <PerSpacer mr="xxxs" />
            <div className="arrows">
              <Button icon="up" disabled={!employee.assessable || index === 0} onClick={() => handleRankInputChange(index, index - 1)} />
              <PerSpacer mb="xxxxs" />
              <Button
                icon="down"
                disabled={
                  !employee.assessable ||
                  (atLeastOneNotAssessableExists(tableData) && index === firstNotAssessableIndex(tableData) - 1) ||
                  index === tableData.length - 1
                }
                onClick={() => handleRankInputChange(index, index + 1)}
              />
            </div>
            <PerSpacer mr="xxxs" />
            <div className="assessable-switch">
              <Tooltip
                title={employee.assessable ? t('employeeList.table.assessableTooltip') : t('employeeList.table.notAssessableTooltip')}
              >
                <Switch
                  data-testid={`assessableSwitch-${employee.id}`}
                  checked={employee.assessable}
                  onChange={(e) => {
                    handleNotAssessableChange(e, employee)
                  }}
                />
              </Tooltip>
            </div>
          </PerFlexRow>
        )
      },
    },
    {
      key: 'previous-ranking-view',
      title: t('rankingView.table.previousRanking.heading', { year: settings.year - 1 }),
      className: 'minWidth-column-s',
      width: '9%',
      sorter: (employeeA: EmployeeWithRankAndTags, employeeB: EmployeeWithRankAndTags) => {
        return !employeeA.previousRank && !employeeB.previousRank
          ? 0
          : !employeeA.previousRank
          ? 1
          : !employeeB.previousRank
          ? -1
          : employeeA.previousRank - employeeB.previousRank
      },
      render: ({ previousRank }: EmployeeWithRankAndTags) => {
        return (
          <PerFlexRow align="middle">
            <PerText fontWeight="bold">{previousRank || '-'}</PerText>
          </PerFlexRow>
        )
      },
    },
    {
      key: 'assessable-history-view',
      title: t('rankingView.table.assessable.heading'),
      className: 'textAlign-center',
      width: '9%',
      render: ({ assessable }: EmployeeWithRankAndTags) => {
        return (
          <PerIcon
            tooltip={
              assessable ? t('rankingView.table.assessable.assessableTooltip') : t('rankingView.table.assessable.notAssessableTooltip')
            }
            name={assessable ? 'check_stroke' : 'close_stroke'}
            color={assessable ? 'success' : 'error'}
            variant="decoration"
            size={16}
          />
        )
      },
    },
    {
      key: 'employee',
      width: isEditingProcess ? '12%' : talentsSeasonStatus !== TalentsSeasonStatus.Closed ? '19%' : '23%',
      title: employeeTableHeader,
      sorter: ({ employee: employeeA }: EmployeeWithRankAndTags, { employee: employeeB }: EmployeeWithRankAndTags) => {
        const nameA = computeFullname(user.nameDisplayPreference, employeeA.firstName, employeeA.lastName)
        const nameB = computeFullname(user.nameDisplayPreference, employeeB.firstName, employeeB.lastName)
        return nameA.localeCompare(nameB)
      },
      render: ({ employee }: EmployeeWithRankAndTags) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={employee.id}
          firstName={employee.firstName}
          lastName={employee.lastName}
          infoText={t(`employee.contract.level.${employee.currentLevel}`)}
          linkToProfile={
            (isCompanyManager || isAllCompaniesSettings || isHRSettings || isYourCompanyView) && employee.id !== user.employeeId
              ? {
                  resigned: false,
                }
              : undefined
          }
          tooltip={
            (isCompanyManager || isAllCompaniesSettings || isHRSettings || isYourCompanyView) && employee.id !== user.employeeId
              ? t('employeeList.table.viewProfile')
              : undefined
          }
        />
      ),
    },
    {
      key: 'bu',
      title: t('rankingView.table.businessUnit.heading'),
      width: '9%',
      sorter: ({ employee: employeeA }: EmployeeWithRankAndTags, { employee: employeeB }: EmployeeWithRankAndTags) => {
        return employeeA.buName.localeCompare(employeeB.buName)
      },
      render: ({ employee }: EmployeeWithRankAndTags) => employee.buName,
    },
    {
      key: 'evaluation',
      title: t('rankingView.table.evaluation.heading'),
      width: '10%',
      sortDirections: ['ascend', 'descend'] as SortOrder[],
      sorter: ((employeeA: EmployeeWithRankAndTags, employeeB: EmployeeWithRankAndTags, sortDirection: SortOrder) => {
        const evaluationEmployeeA = employeeA.draftEvaluation || employeeA.evaluation || 0
        const evaluationEmployeeB = employeeB.draftEvaluation || employeeB.evaluation || 0
        const comparison = evaluationEmployeeA - evaluationEmployeeB
        if (comparison === 0) {
          const fullNameA = computeFullname(user.nameDisplayPreference, employeeA.employee.firstName, employeeA.employee.lastName)
          const fullNameB = computeFullname(user.nameDisplayPreference, employeeB.employee.firstName, employeeB.employee.lastName)
          return sortDirection === 'ascend' ? fullNameA.localeCompare(fullNameB) : fullNameB.localeCompare(fullNameA)
        }
        return comparison
      }) as CompareFn<EmployeeWithRankAndTags>,
      render: ({ draftEvaluation, evaluation, latestEvaluation }: EmployeeWithRankAndTags) => {
        return (
          <PerFlexRow align="middle">
            <PerText tooltip={draftEvaluation ? t('rankingView.table.evaluation.tooltip') : undefined}>
              {draftEvaluation ? `${draftEvaluation.toFixed(2)}*` : evaluation ? `${evaluation.toFixed(2)}` : '-'}
            </PerText>
            {talentsSeasonStatus !== TalentsSeasonStatus.Closed && (
              <>
                <PerSpacer mr="xxs" />
                <PerText fontSize="s" appearance="info">
                  {`(${latestEvaluation ? latestEvaluation.toFixed(2) : '-'})`}
                </PerText>
              </>
            )}
          </PerFlexRow>
        )
      },
    },
    {
      key: 'performance',
      title: t('rankingView.table.performance.heading'),
      width: '13%',
      sorter: (A: EmployeeWithRankAndTags, B: EmployeeWithRankAndTags) => {
        const sortColData = (data: EmployeeWithRankAndTags) => {
          const tags = data.tags.filter((tag) => isPerformanceTag(tag.type as EmployeeTagType))
          if (tags.length === 0) {
            return 0
          }
          return (
            tags
              .map((tag) => {
                if (tag.type === EmployeeTagType.Top) {
                  return tags.length > 1 ? -10 * tags.length : -5
                } else if (tag.type === EmployeeTagType.Expert) {
                  return -4
                } else if (tag.type === EmployeeTagType.Sales) {
                  return -3
                } else if (tag.type === EmployeeTagType.Speaker) {
                  return -2
                } else if (tag.type === EmployeeTagType.Coach) {
                  return -1
                } else if (tag.type === EmployeeTagType.Low || tag.type === EmployeeTagType.EntryLevelLow) {
                  return 1
                } else {
                  return 2
                }
              })
              .reduce((c, a) => c + a, 0) / Math.max(1, tags.length)
          )
        }
        return sortColData(A) - sortColData(B)
      },
      render: (rowData: EmployeeWithRankAndTags) => {
        return (
          <>
            {rowData.tags &&
              rowData.tags.length > 0 &&
              rowData.tags
                .filter((t) => isPerformanceTag(t.type as EmployeeTagType))
                .map((tag, index) => {
                  return (
                    <PerSpacer key={index} my="xxxs" display="inline-block">
                      <PerTag color={tagColor(tag.type)}>{t(`global.employeeTags.performance.${tag.type}`)}</PerTag>
                    </PerSpacer>
                  )
                })}
          </>
        )
      },
    },
    {
      key: 'impact-of-loss',
      title: t('rankingView.table.impactOfLoss.heading'),
      width: '9%',
      sorter: (A: EmployeeWithRankAndTags, B: EmployeeWithRankAndTags) => {
        const sortColData = (data: EmployeeWithRankAndTags) => {
          const tags = data.tags.filter((tag) => isImpactOfLossTag(tag.type as EmployeeTagType))
          if (tags.length === 0) {
            return 0
          }
          return (
            tags
              .map((tag) => {
                if (tag.type === EmployeeTagType.ImpactOfLossCritical) {
                  return -4
                } else if (tag.type === EmployeeTagType.ImpactOfLossSignificant) {
                  return -3
                } else if (tag.type === EmployeeTagType.ImpactOfLossModerate) {
                  return -2
                } else if (tag.type === EmployeeTagType.ImpactOfLossMinor) {
                  return -1
                } else {
                  return 1
                }
              })
              .reduce((c, a) => c + a, 0) / Math.max(1, tags.length)
          )
        }
        return sortColData(A) - sortColData(B)
      },
      render: (rowData: EmployeeWithRankAndTags) => {
        return (
          <>
            {rowData.tags &&
              rowData.tags
                .filter((t) => isImpactOfLossTag(t.type as EmployeeTagType))
                .map((tag, index) => {
                  return (
                    <React.Fragment key={index}>
                      <PerTag color={tagColor(tag.type)}>{t(`global.employeeTags.impactOfLoss.${tag.type}`)}</PerTag>
                    </React.Fragment>
                  )
                })}
          </>
        )
      },
    },
    {
      key: 'risk-of-loss',
      title: t('rankingView.table.riskOfLoss.heading'),
      width: '9%',
      sorter: (A: EmployeeWithRankAndTags, B: EmployeeWithRankAndTags) => {
        const sortColData = (data: EmployeeWithRankAndTags) => {
          const tags = data.tags.filter((tag) => isRiskOfLossTag(tag.type as EmployeeTagType))
          if (tags.length === 0) {
            return 0
          }
          return (
            tags
              .map((tag) => {
                if (tag.type === EmployeeTagType.RiskOfLossVeryHigh) {
                  return -4
                } else if (tag.type === EmployeeTagType.RiskOfLossHigh) {
                  return -3
                } else if (tag.type === EmployeeTagType.RiskOfLossMedium) {
                  return -2
                } else if (tag.type === EmployeeTagType.RiskOfLossLow) {
                  return -1
                } else {
                  return 1
                }
              })
              .reduce((c, a) => c + a, 0) / Math.max(1, tags.length)
          )
        }
        return sortColData(A) - sortColData(B)
      },
      render: (rowData: EmployeeWithRankAndTags) => {
        return (
          <>
            {rowData.tags &&
              rowData.tags
                .filter((t) => isRiskOfLossTag(t.type as EmployeeTagType))
                .map((tag, index) => {
                  return (
                    <React.Fragment key={index}>
                      <PerTag color={tagColor(tag.type)}>{t(`global.employeeTags.riskOfLoss.${tag.type}`)}</PerTag>
                    </React.Fragment>
                  )
                })}
          </>
        )
      },
    },
  ]
    .filter((column) => (isEditingProcess && column.key !== 'ranking-view') || (!isEditingProcess && column.key !== 'ranking-edit'))
    .filter((column) => (talentsSeasonStatus !== TalentsSeasonStatus.Closed ? column.key !== 'assessable-history-view' : true))

  const actionColumn = {
    key: 'actions',
    title: t('rankingView.table.actions.heading'),
    className: 'textAlign-left actions-small',
    width: '13%',
    render: (rowData: EmployeeWithRankAndTags) => {
      const employeeWithTags = { ...rowData.employee, tags: rowData.tags } as EmployeeAndTag
      const employeeFullName = computeFullname(user.nameDisplayPreference, rowData.employee.firstName, rowData.employee.lastName)

      return (
        <RankingTableActionButtons
          employeeFullName={employeeFullName}
          isRankingEnabled
          employeeAndTagData={employeeWithTags}
          settingsTopPercentage={topPercentage}
          isHRSettings={isHRSettings}
          isAllCompaniesSettings={isAllCompaniesSettings}
          isYourCompanyView={isYourCompanyView}
          isCompanyManager={isCompanyManager}
          setModalOpenRemoveLow={setModalOpenRemoveLow}
          setModalDataRemoveLow={setModalDataRemoveLow}
          setModalDataAddEntryLevelLow={setModalDataAddEntryLevelLow}
          setModalOpenTagHistory={(open: boolean) => {
            setModalOpenTagHistory(open)
            setModalTagHistoryEmployeeId(rowData.id)
          }}
          companyId={companyId}
          country={country}
        />
      )
    },
  }

  const columns = talentsSeasonStatus !== TalentsSeasonStatus.Closed ? [...dataColumns, actionColumn] : dataColumns

  return (
    <>
      {modalDataRemoveLow && (
        <CompanySettingsRankingRemoveLowTagModal
          isOpen={modalOpenRemoveLow}
          onSubmit={removeEmployeeTag}
          modalData={modalDataRemoveLow}
          onCancel={onRemoveLowModalCancel}
        />
      )}
      {modalDataAddEntryLevelLow && (
        <AddEntryLevelLowTagAssignmentModal
          onClose={onAddEntryLevelLowModalCancel}
          employeeFullName={computeFullname(
            user.nameDisplayPreference,
            modalDataAddEntryLevelLow.firstName,
            modalDataAddEntryLevelLow.lastName,
          )}
          employeeFirstName={modalDataAddEntryLevelLow.firstName}
          employeeLastName={modalDataAddEntryLevelLow.lastName}
          employeeHasFutureEmploymentChange={employeeHasFutureEmploymentChange}
          employeeHasOngoingAppraisal={employeeHasOngoingAppraisal}
          companyId={companyId}
          country={country}
          employeeId={modalDataAddEntryLevelLow.id}
        />
      )}
      {modalTagHistoryEmployeeId && (
        <LazyRankingTagHistoryModal
          isOpen={modalOpenTagHistory}
          onCancel={onTagHistoryModalCancel}
          employee={data.filter((e) => e.id === modalTagHistoryEmployeeId)[0].employee as EmployeeDisplayTinyInfo}
        />
      )}
      <DndContext collisionDetection={closestCorners} onDragEnd={handleDragEnd}>
        <SortableContext
          items={[...tableData.filter(({ assessable }) => assessable)].map((item) => item.id)}
          strategy={verticalListSortingStrategy}
          disabled={!isEditingProcess}
        >
          <Table
            className={classNames('scroll-compensation light small noTopRadius noShadow', {
              viewWithDoubleTabsAndInfoBoxAndMessageBoxAndEditImportExportBox:
                ((isAllCompaniesSettings || isHRSettings || isYourCompanyView) && !disclaimerClosed) ||
                (!(isAllCompaniesSettings || isHRSettings || isYourCompanyView) &&
                  !isCompanyManager &&
                  !disclaimerClosed &&
                  talentsSeasonStatus === TalentsSeasonStatus.Opened) ||
                (!(isAllCompaniesSettings || isHRSettings || isYourCompanyView) &&
                  !disclaimerClosed &&
                  talentsSeasonStatus !== TalentsSeasonStatus.Opened),
              viewWithDoubleTabsAndInfoBoxAndMessageBoxAndEditImportExportBoxAndConfirmButton:
                !(isAllCompaniesSettings || isHRSettings || isYourCompanyView) &&
                isCompanyManager &&
                !disclaimerClosed &&
                talentsSeasonStatus === TalentsSeasonStatus.Opened,
              viewWithDoubleTabsAndInfoBoxAndEditImportExportBox:
                ((isAllCompaniesSettings || isHRSettings || isYourCompanyView) && disclaimerClosed) ||
                (!(isAllCompaniesSettings || isHRSettings || isYourCompanyView) &&
                  !isCompanyManager &&
                  disclaimerClosed &&
                  talentsSeasonStatus === TalentsSeasonStatus.Opened) ||
                (!(isAllCompaniesSettings || isHRSettings || isYourCompanyView) &&
                  disclaimerClosed &&
                  talentsSeasonStatus !== TalentsSeasonStatus.Opened),
              viewWithDoubleTabsAndInfoBoxAndEditImportExportBoxAndConfirmButton:
                !(isAllCompaniesSettings || isHRSettings || isYourCompanyView) &&
                isCompanyManager &&
                disclaimerClosed &&
                talentsSeasonStatus === TalentsSeasonStatus.Opened,
            })}
            rowKey="id"
            dataSource={tableData}
            components={{ body: { row: DraggableTableRow } }}
            rowClassName={() => 'minHeight-row'}
            columns={columns}
            pagination={false}
            scroll={{ y: true }}
            locale={{
              emptyText:
                talentsSeasonStatus === TalentsSeasonStatus.Ready ? (
                  <PerEmpty
                    heading={t('rankingView.table.disabled.heading', { year })}
                    info={isHRSettings ? t('rankingView.table.disabled.info', { year }) : undefined}
                  />
                ) : talentsSeasonStatus === TalentsSeasonStatus.Opened && !tableData.length ? (
                  <PerEmpty heading={t('rankingView.table.empty.heading')} />
                ) : null,
            }}
          />
        </SortableContext>
      </DndContext>
    </>
  )
}
